// Central theme registry for semantic tokens and branded theme families.

export type ThemeMode = 'light' | 'dark';
export type ThemeSelection = ThemeMode | 'system';
export type ThemeFamilyId = 'sportbook';

export type ThemeVariantFallback = {
  familyId: ThemeFamilyId;
  mode: ThemeMode;
};

export type ThemeVariantDefinition = {
  mode: ThemeMode;
  rootClass: ThemeMode;
  fallbackFrom?: ThemeVariantFallback;
};

export type ThemeFamilyDefinition = {
  id: string;
  label: string;
  modes: Record<ThemeMode, ThemeVariantDefinition>;
};

export const themeStorageKey = 'theme';
export const themeFamilyStorageKey = 'theme-family';

export const themeFamilies: Record<ThemeFamilyId, ThemeFamilyDefinition> = {
  sportbook: {
    id: 'sportbook',
    label: 'SportBook',
    modes: {
      light: {
        mode: 'light',
        rootClass: 'light',
      },
      dark: {
        mode: 'dark',
        rootClass: 'dark',
      },
    },
  },
};

export const defaultThemeFamilyId: ThemeFamilyId = 'sportbook';
export const themeFamilyOptions = Object.values(themeFamilies);

export const themeModes: ThemeMode[] = ['light', 'dark'];

export const getThemeFamily = (familyId: ThemeFamilyId) => themeFamilies[familyId];

export const resolveThemeMode = (selection: ThemeSelection, systemMode: ThemeMode): ThemeMode =>
  selection === 'system' ? systemMode : selection;

export const resolveThemeVariant = (
  familyId: ThemeFamilyId,
  selection: ThemeSelection,
  systemMode: ThemeMode,
) => {
  const family = getThemeFamily(familyId);
  const mode = resolveThemeMode(selection, systemMode);
  const directVariant = family.modes[mode];
  const fallbackVariant = directVariant.fallbackFrom
    ? getThemeFamily(directVariant.fallbackFrom.familyId).modes[directVariant.fallbackFrom.mode]
    : directVariant;

  return {
    familyId,
    familyLabel: family.label,
    mode,
    rootClass: fallbackVariant.rootClass,
    fallbackFrom: directVariant.fallbackFrom,
  };
};
